import type { HapticSummaryKind } from "@/libs/games/haptic-summary.server";

/**
 * 요약 줄 종류 → 어드민 Haptic Effects 카테고리(마케팅 21종, 2026-10-26 운영 정리).
 *
 * 카드의 줄을 누르면 「전체 보기」 시트를 열고 이 카테고리들을 앞에 강조한다 — 요약이 원본(이펙트)을 가리지 않고
 * 근거로 이어지게(아마존 「Customers say」 칩 → 원문, NN/g AI 요약 연구). 카테고리 목록의 정본은 어드민이고,
 * 여기 없는 이름은 강조되지 않을 뿐 시트에는 그대로 나온다. `Signature Effects`(분류 밖 묶음)는 어느 종류에도 넣지 않는다.
 */
export const KIND_EFFECT_CATEGORIES: Record<HapticSummaryKind, string[]> = {
  impact: [
    "Hits & Damage",
    "Explosions & Blasts",
    "Impacts & Rumble",
    "Creature Encounters",
  ],
  action: [
    "Gunfire & Recoil",
    "Reload & Weapon Handling",
    "Melee Combat",
    "Magic & Powers",
    "Hands-on Interactions",
  ],
  movement: [
    "Movement & Traversal",
    "Engine & Road Feel",
    "Flight & Space",
    "Thrill Rides",
  ],
  status: ["Heartbeat & Vitals", "Sensory Pulses"],
  world: [
    "World & Environment",
    "Key Moments",
    "Cinematic Moments",
    "Music & Rhythm",
    "Menu & UI Feedback",
  ],
  touch: ["Hands-on Interactions"],
};

/**
 * 표준 21종 밖의 이름 — 운영이 손으로 입력해 둔 게임은 카테고리가 `Gun Recoil` · `Directional Damage` 처럼 자유 이름이다
 * (prod 7개, 2026-10-27: counter-strike-2 · dungeons-of-eternity · galactic-repair · into-the-radius-vr · medieval-dynasty-new-settlement ·
 * niraya-of · vail-vr — 확인: by-filters 응답에서 `hapticEffects[].category` 가 아래 표에 없는 게임). 그 이름에 든 단어로 맞춘다.
 *
 * ★**단어의 시작에서만** 맞춘다(`\b`) — 부분 문자열이면 `Drunk`→run · `Island`→land · `Spatial`→pat · `Override`→ride 가 걸린다(#6083 R1).
 * 짧은 단어(`land` · `run` · `ride` · `pat` · `hug`)는 단어 전체로 — `Landmine` · `Patrol`.
 */
const KIND_KEYWORDS: Record<HapticSummaryKind, RegExp> = {
  impact:
    /\b(damage|hit|explosion|blast|impact|flashbang|fatal|death|bite|rumble|(eaten|frozen)\b|ice breaking)/i,
  action:
    /\b(recoil|gun|shoot|reload|melee|weapon|magic|spell|attack|punch|kill|slash|chop|cut\b|bow\b|arrows?\b|throw|draw\b)/i,
  movement:
    /\b(jump|move|movement|climb|(land|run|ride)(s|ing)?\b|fly|flight|drive|fall|zipline|teleport|haste)/i,
  status:
    /\b(heart|vital|heal|medi|health|stamina|drink|eat\b|hunger|thirst|poi?sion|drunk|injection|invisible)/i,
  world:
    /\b(world|environment|weather|ambient|music|rhythm|moment|cinematic|rain\b|scan|win\b|lose\b|level up|power up|gain coin|open box|escapes?\b|omens?\b)/i,
  touch: /\b(pat|hug)s?\b/i, // 사람과 사람의 접촉 — 물건 잡기(`Grab`) · 액체 닿음(`Liquid Touch`)은 아니다
};

/**
 * 그 게임 이펙트 중 이 종류에 해당하는 카테고리 — 없으면 빈 배열(줄을 누를 수 없게 그린다).
 *
 * ★맞는 이름이 없으면 **`Signature Effects`** 로 간다 — 운영 규칙상 이름 있는 카테고리는 최대 5개이고 6번째 이하는 이 묶음으로
 * 합쳐진다(보고서 `prod-workspace-haptics-report-2026-10-26` §3). 그래서 그 종류의 근거 이벤트는 대개 이 안에 있다.
 * 실측(2026-10-27): 미연결 151줄 중 89줄이 이 경우였다(52줄은 이펙트 섹션이 없는 SimHub, 10줄은 묶음도 없음).
 */
export const matchingEffectCategories = (
  kind: HapticSummaryKind,
  gameCategories: string[],
): string[] => {
  const wanted = new Set(KIND_EFFECT_CATEGORIES[kind] ?? []);
  const standard = new Set(Object.values(KIND_EFFECT_CATEGORIES).flat());
  const isSignature = (c: string) => /^signature\b/i.test(c);
  const named = gameCategories.filter(
    (c) =>
      wanted.has(c) ||
      (!standard.has(c) && !isSignature(c) && KIND_KEYWORDS[kind].test(c)),
  );
  if (named.length > 0) return named;
  return gameCategories.filter(isSignature);
};
